import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { AIRO } from "@/constants/testIds";

const LINKS = [
  { to: "/what-we-build", label: "Services", tid: AIRO.navWhatWeBuild },
  { to: "/how-it-works", label: "How It Works", tid: AIRO.navHow },
  { to: "/insights", label: "Insights", tid: "nav-insights" },
  { to: "/our-story", label: "About", tid: AIRO.navStory },
];

export const Nav = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#141414]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 h-[74px] flex items-center justify-between">
        <NavLink to="/" data-testid={AIRO.navHome} className="flex items-center" aria-label="AIROTECH home">
          <img src="/airotech-mark.png" alt="AIROTECH" className="h-8 w-auto" />
        </NavLink>

        <nav className="hidden lg:flex items-center gap-8 mono-label">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              className={({ isActive }) =>
                `transition-colors duration-200 ${isActive ? "text-cyan-400" : "text-zinc-400 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={AIRO.navStart}
            onClick={() => navigate("/start")}
            className="hidden sm:inline-flex items-center gap-2 mono-label bg-white text-black px-5 py-2.5 rounded-full font-medium hover:bg-cyan-400 hover:shadow-[0_0_18px_rgba(0,229,255,0.35)] transition-all duration-300"
          >
            Start Your Build <ArrowRight className="h-3.5 w-3.5" />
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid={AIRO.navMenu} className="lg:hidden text-white p-2" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#070707] border-[#141414] text-white w-[300px]">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <img src="/airotech-mark.png" alt="AIROTECH" className="h-8 w-auto mb-10 mt-2" />
              <nav className="flex flex-col gap-1">
                {LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `py-3 border-b border-[#141414] mono-label ${isActive ? "text-cyan-400" : "text-zinc-300"}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <button
                    onClick={() => navigate("/start")}
                    className="mt-8 inline-flex items-center justify-center gap-2 mono-label bg-white text-black px-5 py-3.5 rounded-full font-medium"
                  >
                    Start Your Build <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </SheetClose>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
